import { Box, Typography } from '@mui/material';

import S3Avatar from '../../../components/S3Avatar';
import { SHRED_COLORS, SHRED_FONTS } from '../constants';
import { useCountUp, useStableHeight } from '../hooks';
import { getRankDelta, resolveAvatarSrc } from '../helpers';

// Sign-based colour for improvement percentages; exactly zero (e.g. a rookie still setting a baseline) is neutral.
export const pctColor = (value) =>
  value > 0 ? SHRED_COLORS.positive : value < 0 ? SHRED_COLORS.negative : SHRED_COLORS.muted;

export const focusRing = {
  '&:focus-visible': { outline: `2px solid ${SHRED_COLORS.gold}`, outlineOffset: 2 },
};

export const CountUp = ({ value, format = (v) => String(Math.round(v)), reduced }) => {
  const animated = useCountUp(value, { disabled: reduced });
  return <>{format(animated)}</>;
};

export const StableHeight = ({ active, children }) => {
  const ref = useStableHeight(active);
  return <Box ref={ref}>{children}</Box>;
};

export const SectionTitle = ({ kicker, title, hint, id }) => (
  <Box sx={{ mb: 2.5 }}>
    {kicker && (
      <Typography
        sx={{
          fontFamily: SHRED_FONTS.numeric,
          fontSize: 12,
          letterSpacing: '0.2em',
          color: SHRED_COLORS.flame,
          textTransform: 'uppercase',
          mb: 0.5,
        }}
      >
        {kicker}
      </Typography>
    )}
    <Typography
      id={id}
      component="h2"
      sx={{
        fontFamily: SHRED_FONTS.display,
        fontWeight: 800,
        fontSize: { xs: 32, md: 44 },
        lineHeight: 1,
        textTransform: 'uppercase',
        color: SHRED_COLORS.text,
      }}
    >
      {title}
    </Typography>
    {hint && (
      <Typography sx={{ color: SHRED_COLORS.muted, fontSize: 14, mt: 0.75, fontFamily: SHRED_FONTS.body }}>
        {hint}
      </Typography>
    )}
  </Box>
);

export const Panel = ({ children, sx, ...rest }) => (
  <Box
    {...rest}
    sx={{
      background: SHRED_COLORS.panel,
      border: `1px solid ${SHRED_COLORS.line}`,
      borderRadius: '14px',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const LevelChip = ({ level, sx }) => !level ? null : (
  <Box
    component="span"
    sx={{
      flex: 'none',
      fontFamily: SHRED_FONTS.numeric,
      fontSize: 11,
      lineHeight: 1,
      px: 0.75,
      py: '3px',
      borderRadius: '5px',
      background: 'rgba(255,255,255,0.08)',
      color: SHRED_COLORS.muted,
      border: `1px solid ${SHRED_COLORS.line}`,
      ...sx,
    }}
  >
    LV {level}
  </Box>
);

// Members who are tracked but cannot place (for example employees) carry a label instead of a rank.
export const IneligibleChip = ({ label, sx }) =>
  !label ? null : (
    <Box
      component="span"
      sx={{
        flex: 'none',
        fontFamily: SHRED_FONTS.numeric,
        fontSize: 10,
        lineHeight: 1,
        letterSpacing: '0.08em',
        px: 0.75,
        py: '3px',
        borderRadius: '5px',
        color: SHRED_COLORS.gold,
        border: `1px dashed ${SHRED_COLORS.gold}`,
        ...sx,
      }}
    >
      {label}
    </Box>
  );

const AVATAR_HUES = [12, 24, 36, 4, 18, 30];

// The photo sits on top of an initial so members without a profile picture (404) still look intentional.
export const MemberAvatar = ({ id, name = '', size = 36, ring }) => {
  const hue = AVATAR_HUES[String(id).length % AVATAR_HUES.length] + (String(id).charCodeAt(String(id).length - 1) % 9) * 3;
  return (
    <Box
      sx={{
        flex: 'none',
        width: size,
        height: size,
        borderRadius: '50%',
        p: ring ? '2px' : 0,
        background: ring || 'transparent',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          background: `linear-gradient(135deg, hsl(${hue} 70% 38%), hsl(${hue} 60% 22%))`,
          color: SHRED_COLORS.text,
          fontFamily: SHRED_FONTS.display,
          fontWeight: 800,
          fontSize: size * 0.45,
          lineHeight: 1,
        }}
      >
        <span aria-hidden="true">{String(name).charAt(0).toUpperCase()}</span>
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <S3Avatar src={resolveAvatarSrc(id)} />
        </Box>
      </Box>
    </Box>
  );
};

const MOVE_STYLES = {
  up: { color: SHRED_COLORS.positive, glyph: '▲' },
  down: { color: SHRED_COLORS.negative, glyph: '▼' },
  same: { color: SHRED_COLORS.muted, glyph: '–' },
  new: { color: SHRED_COLORS.gold, glyph: 'NEW' },
};

export const RankMove = ({ entry }) => {
  const { direction, amount } = getRankDelta(entry);
  const style = MOVE_STYLES[direction];
  if (!style) return <Box sx={{ width: 34 }} />;
  const label =
    direction === 'up'
      ? `Up ${amount} ${amount === 1 ? 'place' : 'places'}`
      : direction === 'down'
      ? `Down ${amount} ${amount === 1 ? 'place' : 'places'}`
      : direction === 'new'
      ? 'New on the ranked board'
      : 'No change';
  return (
    <Box
      role="img"
      aria-label={label}
      sx={{
        width: 34,
        textAlign: 'left',
        fontFamily: SHRED_FONTS.numeric,
        fontSize: 11,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {style.glyph}
      {amount > 0 ? ` ${amount}` : ''}
    </Box>
  );
};

// Ring that fills toward the qualification minimum. The number inside is sessions done.
export const QualifyRing = ({ done, total, size = 40, label }) => {
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = total > 0 ? Math.min(1, done / total) : 0;
  return (
    <Box
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      sx={{ position: 'relative', width: size, height: size, flex: 'none' }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={SHRED_COLORS.flame}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="shred-motion"
          style={{ transition: 'stroke-dashoffset 700ms ease' }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          fontFamily: SHRED_FONTS.numeric,
          fontSize: size > 44 ? 15 : 12,
          color: SHRED_COLORS.text,
        }}
      >
        {done}/{total}
      </Box>
    </Box>
  );
};

export const Pips = ({ done, total }) => (
  <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', gap: '4px' }}>
    {Array.from({ length: total }, (_, i) => (
      <Box
        key={i}
        component="i"
        sx={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: i < done ? SHRED_COLORS.flame : 'rgba(255,255,255,0.14)',
          boxShadow: i < done ? `0 0 6px ${SHRED_COLORS.flame}` : 'none',
        }}
      />
    ))}
  </Box>
);
